import type { RecordModel } from 'pocketbase';
import type { Layout } from '@kioskforge/shared';
import { pb } from './pocketbase';
import { apiRequest } from './client';

export type PresetRecord = RecordModel & {
  key?: string;
  name?: string;
  sector?: string;
  min_lockdown?: string;
  device_classes?: string[];
  wave?: number;
};

export type LayoutRecord = RecordModel & {
  name?: string;
  status?: string;
  version?: number;
  preset?: string;
  config?: Record<string, unknown>;
  /** The server-compiled layout for this record (used for capability preview). */
  compiled?: Layout;
  expand?: { preset?: { key?: string; name?: string } };
};

export type CreateLayoutResult = {
  layout_id: string;
  name: string;
  preset_key: string;
  version: number;
  status: string;
};

export type PublishLayoutResult = { layout_id: string; version: number; status: string };

/** Wave-1 presets a tenant can build a layout from. */
export async function listPresets(): Promise<PresetRecord[]> {
  const records = await pb.collection('presets').getFullList({ sort: 'key', filter: 'wave = 1' });
  return records as PresetRecord[];
}

/** The tenant's layouts (both drafts and published). */
export async function listLayouts(): Promise<LayoutRecord[]> {
  const records = await pb.collection('layouts').getFullList({ sort: '-created', expand: 'preset' });
  return records as LayoutRecord[];
}

/** Creates a draft layout; `config` is validated by the server against the preset schema. */
export async function createLayout(input: {
  preset_key: string;
  name: string;
  config: Record<string, unknown>;
}): Promise<CreateLayoutResult> {
  return apiRequest<CreateLayoutResult>('/layouts', { method: 'POST', body: input });
}

/** Publishes a layout (optionally with an amended config in the same step). */
export async function publishLayout(id: string, config?: Record<string, unknown>): Promise<PublishLayoutResult> {
  return apiRequest<PublishLayoutResult>(`/layouts/${id}/publish`, {
    method: 'POST',
    body: config === undefined ? {} : { config }
  });
}

export type UpdateLayoutResult = { layout_id: string; name?: string; status: string; version: number };

/** Renames a layout (A5b.2). */
export async function renameLayout(id: string, name: string): Promise<UpdateLayoutResult> {
  return apiRequest<UpdateLayoutResult>(`/layouts/${id}`, { method: 'PATCH', body: { name } });
}

/** Saves an amended draft config (A5b.1) — recompiled, status/version unchanged. */
export async function saveDraftConfig(id: string, config: Record<string, unknown>): Promise<UpdateLayoutResult> {
  return apiRequest<UpdateLayoutResult>(`/layouts/${id}`, { method: 'PATCH', body: { config } });
}

/** Deletes an unused layout (A5b.2). A layout in use rejects with `layout_in_use`. */
export async function deleteLayout(id: string): Promise<{ layout_id: string; deleted: boolean }> {
  return apiRequest<{ layout_id: string; deleted: boolean }>(`/layouts/${id}`, { method: 'DELETE' });
}

export type LayoutUsage = { deployments: number };

/** How many deployments reference this layout — the delete guard's evidence (A5b.2). */
export async function checkLayoutUsage(id: string): Promise<LayoutUsage> {
  const deployments = await pb.collection('deployments').getList(1, 1, { filter: `layout = "${id}"` });
  return { deployments: deployments.totalItems };
}
